import React, { useState } from 'react';
import { X, Check, AlertTriangle, FileText, TrendingUp, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { reviewDocument, applyChangesToDocument, SuggestedChange, DocumentReviewResult } from '../services/documentReview';

interface DocumentReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentContent: string;
  documentType: string;
  documentGenerationId?: string;
  onApplyChanges: (updatedContent: string) => void;
}

export const DocumentReviewModal: React.FC<DocumentReviewModalProps> = ({
  isOpen,
  onClose,
  documentContent,
  documentType,
  documentGenerationId,
  onApplyChanges,
}) => {
  const [isReviewing, setIsReviewing] = useState(false);
  const [reviewResult, setReviewResult] = useState<DocumentReviewResult | null>(null);
  const [selectedChanges, setSelectedChanges] = useState<Set<string>>(new Set());
  const [expandedChange, setExpandedChange] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleStartReview = async () => {
    setIsReviewing(true);
    setError(null);

    try {
      const result = await reviewDocument(documentContent, documentType, documentGenerationId);
      setReviewResult(result);
      setSelectedChanges(new Set(result.suggestedChanges.map(c => c.id)));
    } catch (err) {
      console.error('Review error:', err);
      setError(err instanceof Error ? err.message : 'Failed to review document. Please try again.');
    } finally {
      setIsReviewing(false);
    }
  };

  const handleToggleChange = (changeId: string) => {
    const newSelected = new Set(selectedChanges);
    if (newSelected.has(changeId)) {
      newSelected.delete(changeId);
    } else {
      newSelected.add(changeId);
    }
    setSelectedChanges(newSelected);
  };

  const handleApplySelectedChanges = () => {
    if (!reviewResult) return;

    const changesToApply = reviewResult.suggestedChanges.filter(c => selectedChanges.has(c.id));
    const updatedContent = applyChangesToDocument(documentContent, changesToApply);
    onApplyChanges(updatedContent);
    onClose();
  };

  const getImportanceColor = (importance: string) => {
    switch (importance) {
      case 'critical': return 'text-red-700 bg-red-100 border-red-300';
      case 'high': return 'text-gray-900 bg-gray-100 border-gray-300';
      case 'medium': return 'text-gray-700 bg-gray-50 border-gray-200';
      case 'low': return 'text-gray-500 bg-gray-50 border-gray-200';
      default: return 'text-gray-700 bg-gray-100 border-gray-300';
    }
  };

  const getComplianceColor = (score: number) => {
    if (score >= 90) return 'text-gray-900 bg-gray-100';
    if (score >= 70) return 'text-gray-700 bg-gray-50';
    return 'text-red-700 bg-red-100';
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-hidden">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl flex-shrink-0">
          <div className="flex items-center">
            <Shield className="w-6 h-6 text-gray-900 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Rental Rights Bill 2025 Review</h2>
              <p className="text-sm text-gray-600 mt-1">AI-powered compliance analysis</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isReviewing}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {!reviewResult ? (
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">What We'll Analyze</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-gray-900 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Rental Rights Bill 2025 compliance (periodic tenancy requirements, rent caps, etc.)</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-gray-900 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Tenant rights and protections adequacy</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-gray-900 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Landlord obligations and responsibilities</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-gray-900 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Termination and possession procedures</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-gray-900 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Property condition and safety standards</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-gray-900 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Legal enforceability and clarity</span>
                  </li>
                </ul>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start">
                  <AlertTriangle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-red-800 font-medium">Review Error</p>
                    <p className="text-red-600 text-sm mt-1">{error}</p>
                  </div>
                </div>
              )}

              <div className="flex justify-center">
                <button
                  onClick={handleStartReview}
                  disabled={isReviewing}
                  className="px-8 py-4 bg-gray-900 text-white rounded-lg hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center text-lg shadow-lg"
                >
                  {isReviewing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Analyzing Document...
                    </>
                  ) : (
                    <>
                      <TrendingUp className="w-6 h-6 mr-3" />
                      Start AI Review
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className={`rounded-xl p-6 border ${getComplianceColor(reviewResult.complianceScore)}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Compliance Score</h3>
                  <div className="text-4xl font-bold">{reviewResult.complianceScore}/100</div>
                </div>
                <p className="text-sm">{reviewResult.overallAssessment}</p>
              </div>

              {reviewResult.criticalIssues.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <h4 className="font-semibold text-red-900 mb-3 flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    Critical Issues
                  </h4>
                  <ul className="space-y-2">
                    {reviewResult.criticalIssues.map((issue, index) => (
                      <li key={index} className="text-sm text-red-800 flex items-start">
                        <span className="mr-2">•</span>
                        <span>{issue}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {reviewResult.keyStrengths.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Check className="w-5 h-5 mr-2" />
                    Key Strengths
                  </h4>
                  <ul className="space-y-2">
                    {reviewResult.keyStrengths.map((strength, index) => (
                      <li key={index} className="text-sm text-gray-800 flex items-start">
                        <span className="mr-2">•</span>
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Rental Rights Bill 2025 Compliance</h4>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900 mr-2">Status:</span>
                    <span className={`text-sm px-3 py-1 rounded-full ${reviewResult.rentalRightsBillCompliance.compliant ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'}`}>
                      {reviewResult.rentalRightsBillCompliance.compliant ? 'Compliant' : 'Non-Compliant'}
                    </span>
                  </div>
                  {reviewResult.rentalRightsBillCompliance.violations.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-2">Violations:</p>
                      <ul className="space-y-1">
                        {reviewResult.rentalRightsBillCompliance.violations.map((violation, index) => (
                          <li key={index} className="text-sm text-gray-800 flex items-start">
                            <span className="mr-2">•</span>
                            <span>{violation}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Suggested Changes ({reviewResult.suggestedChanges.length})</h3>
                  <div className="text-sm text-gray-600">
                    {selectedChanges.size} selected
                  </div>
                </div>

                <div className="space-y-3">
                  {reviewResult.suggestedChanges.map((change) => (
                    <div
                      key={change.id}
                      className={`border rounded-lg ${selectedChanges.has(change.id) ? 'border-gray-900 bg-gray-50' : 'border-gray-200 bg-white'}`}
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-start flex-1">
                            <input
                              type="checkbox"
                              checked={selectedChanges.has(change.id)}
                              onChange={() => handleToggleChange(change.id)}
                              className="mt-1 mr-3 w-5 h-5 text-gray-900 rounded"
                            />
                            <div className="flex-1">
                              <div className="flex items-center flex-wrap gap-2 mb-2">
                                <span className="font-semibold text-gray-900">{change.section}</span>
                                <span className={`text-xs px-2 py-1 rounded-full border ${getImportanceColor(change.importance)}`}>
                                  {change.importance.toUpperCase()}
                                </span>
                                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-300">
                                  {change.category}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700 mb-2">{change.reason}</p>
                              <button
                                onClick={() => setExpandedChange(expandedChange === change.id ? null : change.id)}
                                className="text-sm text-gray-900 hover:text-black flex items-center font-medium"
                              >
                                {expandedChange === change.id ? (
                                  <>
                                    <ChevronUp className="w-4 h-4 mr-1" />
                                    Hide details
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="w-4 h-4 mr-1" />
                                    Show changes
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>

                        {expandedChange === change.id && (
                          <div className="mt-4 space-y-3 pl-8">
                            <div className="bg-red-50 border border-red-200 rounded p-3">
                              <p className="text-xs font-medium text-red-900 mb-1">Original:</p>
                              <p className="text-sm text-red-800 font-mono">{change.original}</p>
                            </div>
                            <div className="bg-gray-50 border border-gray-200 rounded p-3">
                              <p className="text-xs font-medium text-gray-900 mb-1">Suggested:</p>
                              <p className="text-sm text-gray-800 font-mono">{change.suggested}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {reviewResult && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row gap-3 rounded-b-2xl flex-shrink-0">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleApplySelectedChanges}
              disabled={selectedChanges.size === 0}
              className="flex-1 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center"
            >
              <FileText className="w-5 h-5 mr-2" />
              Apply {selectedChanges.size} Change{selectedChanges.size !== 1 ? 's' : ''}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
